// /imports/ui/components/course/context.js
import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { createContext, useContextSelector } from 'use-context-selector';
import { fetchCourseDetails, importWhiteboardData, validateUser } from './service'; // Adjust path accordingly

import Auth from '/imports/ui/services/auth';
const CourseContextSelector = createContext(null);

const CourseContextProvider = ({ children }) => {
  const [state, setState] = useState({ course: null });
  const hasFetched = useRef(false);

  const userKey = useMemo(() => {
    if (!Auth?._meetingID || !Auth?._externUserID) return null;
    return `${Auth._meetingID}-${Auth._externUserID}`;
  }, [Auth?._meetingID, Auth?._externUserID]);


  useEffect(() => {
    if (!userKey) return;

    const apiPayload = {
      internalMeetingID: Auth?._meetingID,
      externalUserID: Auth?._externUserID
    }
    // FETCH COURSE DETAILS FROM API
    // Non-blocking: children render immediately with course=null (isPrepareLesson defaults to false).
    // For Meet & Greets, this API call will fail (no course data) — controls show correctly.
    fetchCourseDetails(apiPayload)
      .then((res) => {
        setState({ course: res.data.data, });

        // Set localStorage flag if in classroom preparation mode
        const isPrepareLesson = res?.data?.data?.courseDetail?.isPrepareLesson ?? false;
        if (isPrepareLesson) {
          localStorage.setItem('isPrepareLesson', 'true');
        } else {
          localStorage.setItem('isPrepareLesson', 'false');
        }
        const courseSessionID = res?.data?.data?.courseDetail?.courseSessionId
        localStorage.setItem('courseSessionID', courseSessionID)
        const validationPayload = {
          internalMeetingID: Auth?._meetingID,
          externalUserID: Auth?._externUserID,
          courseSessionId: res?.data?.data?.courseDetail?.courseSessionId,
          lesson: res?.data?.data?.courseDetail?.lesson,
          isPrepareLesson: isPrepareLesson
        }
        // VALIDATING USER
        validateUser(validationPayload)
          .catch(() => {
            setState({ course: null, });
          });
      })
      .catch((err) => {
        console.warn('[CourseContext] fetchCourseDetails failed (expected for Meet & Greets):', err?.message);
        setState({ course: null, });
        localStorage.setItem('isPrepareLesson', 'false');
      }).finally(() => {
        hasFetched.current = true; // mark as fetched
      });
  }, [userKey]);

  const contextValue = useMemo(() => [state], [state]);

  // Always render children — never block on course details fetch.
  // Components read isPrepareLesson from context; it defaults to false when course is null.
  return (
    <CourseContextSelector.Provider value={contextValue}>
      {children}
    </CourseContextSelector.Provider>
  );
};
// Inside same file or exported separately
const courseSelect = (selector) =>
  useContextSelector(CourseContextSelector, (context) =>
    context ? selector(context[0]) : undefined
  );

const courseDispatch = () =>
  useContextSelector(CourseContextSelector, (context) => context[1]); // Not used in this case, but kept for parity


CourseContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {
  CourseContextProvider,
  courseSelect,
  courseDispatch,
};
